import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HorizontalProductCarousel — single-line scrollable product row with arrows.
 * Used for "You May Also Like" and "Recently Viewed".
 */
export default function HorizontalProductCarousel({ title, items, testId }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => { checkScroll(); }, [items]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 280, behavior: "smooth" });
    setTimeout(checkScroll, 350);
  };

  if (!items || items.length === 0) return null;

  return (
    <section className="py-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[18px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
          {title}
        </h2>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => scroll(-1)}
            disabled={!canScrollLeft}
            className="p-1.5 border border-[#282828]/10 hover:border-[#282828]/30 disabled:opacity-20 disabled:cursor-default transition-colors"
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft className="w-4 h-4 text-[#282828]" />
          </button>
          <button
            onClick={() => scroll(1)}
            disabled={!canScrollRight}
            className="p-1.5 border border-[#282828]/10 hover:border-[#282828]/30 disabled:opacity-20 disabled:cursor-default transition-colors"
            data-testid={`${testId}-next`}
          >
            <ChevronRight className="w-4 h-4 text-[#282828]" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-3 overflow-x-auto scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {items.map(item => {
          const price = item.price_4_day || item.rental_options?.[0]?.price || item.price || 0;
          return (
            <Link
              key={item.id}
              to={`/outfit/${item.id}`}
              className="group flex-shrink-0 w-[160px] sm:w-[180px]"
              data-testid={`carousel-item-${item.id}`}
            >
              <div className="aspect-[3/4] bg-[#f5f3f0] overflow-hidden mb-1.5">
                <img
                  src={item.images?.[0] || item.image || ""}
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <p className="text-[12px] text-[#282828] truncate">{item.name}</p>
              {price > 0 && <p className="text-[11px] text-[#9A7B4F]">From ${price}</p>}
            </Link>
          );
        })}
      </div>
    </section>
  );
}
